<script setup>

import Clip from "@/Components/Icons/Clip.vue";
import Send from "@/Components/Icons/Send.vue";
import Badge from "@/Components/Badge.vue";
import MessageString from "@/Components/Messages/MessageString.vue";
import MessageSystem from "@/Components/Messages/MessageSystem.vue";
import MessageDate from "@/Components/Messages/MessageDate.vue";
import dayjs from "dayjs";
import Input from "@/Components/Input.vue";
import {computed, nextTick, onMounted, ref, shallowRef, watch} from "vue";
import Modal from "@/Components/Modal.vue";
import AkLogo from "@/Components/Icons/AkLogo.vue";
import {Link, router, useForm, usePage} from "@inertiajs/vue3";
import {useThread} from "@/Store/Thread.js";
import {storeToRefs} from "pinia";
import Loading from "@/Components/Loading.vue";
import {route} from "ziggy-js";
import debounce from "lodash-es/debounce.js";
import Button from "@/Components/Button.vue";
import {activeCurrency, priceStr, setMessages} from "@/helper.js";
import {useUser} from "@/Store/User.js";
import StarReview from "@/Components/Icons/StarReview.vue";

const props = defineProps({
    contentClasses: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false,
    },
    viewOffer: {
        type: Object,
        default: null
    }
})


const showModal = ref(false),
    dataImg = ref(null),
    files = ref([]),
    upload = ref(null),
    page = usePage(),
    {thread, messages, dialogElement, inputs, selectedUser} = storeToRefs(useThread()),
    {getUserStatusByIdOrStatus} = storeToRefs(useUser()),
    currentPageMessage = ref(1),
    isLoading = ref(false),
    offer = ref(thread.value?.user?.offer),
    oldHeight = ref(null),
    needAuth = ref(false),
    inputMsg = ref(null)

const formMessage = useForm({
    message: thread.value ?
        (inputs.value[thread.value.id] ? inputs.value[thread.value.id] : '') :
        (inputs.value['untitled'] ? inputs.value['untitled'] : ''),
    user_id: undefined,
})
const formFile = useForm({
    user_id: undefined,
    file: undefined
})
const fetch = (nextPage = false) => {
    if (!nextPage) {
        currentPageMessage.value = 1
    }
    isLoading.value = true


    axios.get(route('threads.show', {id: thread.value.id}) + `?message_page=${currentPageMessage.value}`, {
        headers: {
            "Content-type": 'application/json',
            "X-Inertia": true,
            "X-Inertia-Partial-Component": 'Thread/Index',
            "X-Inertia-Partial-Data": 'messages',
            "X-Inertia-Version": page.version
        }
    }).then((resp) => {
        setMessages([...resp.data.props.messages.data, ...Object.values(messages.value)])
        nextTick().then(() => {
            dialogElement.value.scrollBy(0, dialogElement.value.scrollHeight - oldHeight.value)
            oldHeight.value = dialogElement.value.scrollHeight
        })
    }).finally(() => {
        isLoading.value = false
    })
}
const fetchDebounced = debounce(fetch, 1000)

const nextPageFunc = () => {
    if (currentPageMessage.value < page.props.messages.last_page) {
        currentPageMessage.value++
        fetch(true)
    }
}


const haveChat = computed(() => {
    if (page.props.threads) {
        return page.props.threads.data.length > 0;
    }
    return false;
})

function inputFilter(newFile, oldFile, prevent) {
    if (!page.props.auth.user) {
        needAuth.value = true
        return false;
    }
    if (newFile && !oldFile) {
        if (newFile.type === 'application/pdf' || newFile.type.substr(0, 6) === 'image/') {
            formFile.file = newFile.file
            sendFile()
            upload.value.active = true
        }
        // Before adding a file
        // Filter system files or hide files
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
            return prevent()
        }

        // Filter php html js file
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
            return prevent()
        }
    }
}

const dateComparison = (pre, current) => {

    if (dayjs(pre).isValid() && typeof pre !== 'undefined') {
        return dayjs(pre).format('YYYY-MM-DD') !== dayjs(current).format('YYYY-MM-DD')
    } else {
        return true;
    }
}

const createChat = (userId) => {
    if(!inputs.value['untitled']) return;
    router.post(route('admin.messages.store'), {
            user_id: userId,
            message: inputs.value['untitled'],
        },
        {
            preserveState: true,
            preserveScroll: true,
            only: ['thread', 'messages'],
            onFinish: () => {
                thread.value = page.props.thread;
                setMessages(page.props.messages.data);
            }
        })
}

const sendMsg = () => {
    if(!thread.value && !selectedUser.value?.id) return;
    if(!thread.value && selectedUser.value?.id) {
        createChat(selectedUser.value?.id);
        inputs.value['untitled'] = '';
        formMessage.reset()
    } else {
        formMessage.user_id = thread.value.user?.id
        if (formMessage.message) {
            if (!route().current('admin.threads.*')) {
                axios.post(route('threads.store'), {
                    message: formMessage.message,
                    user_id: formMessage.user_id,
                    offer_id: props.viewOffer ? props.viewOffer.id : undefined
                }).then(() => {
                    formMessage.reset()
                });
            } else {
                axios.post(route('admin.threads.store', thread.value), {
                    message: formMessage.message
                }).then(() => {
                    formMessage.reset()
                });
            }
        }
    }

}

const sendFile = () => {
    if (formFile.file) {
        let form = new FormData();
        form.append('file', formFile.file)
        if (!route().current('admin.threads.*')) {
            form.append('user_id', thread.value.user.id)
            if (props.viewOffer) {
                form.append('offer_id', props.viewOffer.id)
            }
            axios.post(route('threads.store'), form).then(() => {
                formFile.reset()
            });
        } else {
            axios.post(route('admin.threads.store', thread.value), form).then(() => {
                formFile.reset()
            });
        }

    }
}


defineExpose({
    dialogElement
})

const showImg = ($event) => {
    showModal.value = true
    dataImg.value = $event
}

const isAuth = () => {
    if (!page.props.auth.user) {
        inputMsg.value.querySelector('input[type="text"]').blur()
        needAuth.value = true
    }
}

const closeModal = () => {
    showModal.value = false
    dataImg.value = null
}
onMounted(() => {
    dialogElement.value.addEventListener('scroll', () => {
        if (dialogElement.value.scrollTop === 0) {
            nextPageFunc()
        }
    })
    oldHeight.value = dialogElement.value.scrollHeight
    dialogElement.value.scrollBy(0, dialogElement.value.scrollHeight)
})

const isSystem = (message) => {
    return message.type !== 'user';
}

watch(() =>
        formMessage.message, (val) => {
        if(!thread.value) {
            inputs.value['untitled'] = formMessage.message;
        } else {
            inputs.value[thread.value.id] = formMessage.message;
        }
        if (val.length > 1 && !page.props.auth.user) {
            isAuth()
        }
    }
)

const getStyleStarReview = (rating) => {
    return rating <= 2 ? 'fill-red-500' : rating <= 3 ? 'fill-orange-500' : rating <= 4 ? 'fill-yellow-400' : 'fill-green-500'
}

const VueUploadComponent = shallowRef(null);
onMounted(async () => {
    if (typeof window !== 'undefined') {
        const module = await import("vue-upload-component");
        VueUploadComponent.value = module.default;
    }
});

</script>

<template>
    <div :class="contentClasses"
         class="rounded-lg bg-slate-200 dark:bg-slate-800 px-2.5 py-3 shadow-md flex flex-col gap-2.5 grow">
        <div class="flex justify-between items-center pr-1">
            <template v-if="!route().current('admin.threads.*')">
                <div class="flex gap-2.5">
                    <div class="flex gap-4 items-center pl-2">
                        <slot name="left"></slot>
                        <div
                            class="rounded-full border border-gray-400 shadow-md-1 min-h-[3.75rem] min-w-[3.75rem] overflow-hidden bg-slate-300 dark:bg-slate-700">
                            <template v-if="thread?.user">
                                <img class="w-[3.75rem] h-[3.75rem]" :src="thread.user.profile_photo_url" :alt="thread.user.name">
                            </template>
                        </div>
                    </div>

                    <div v-if="thread?.user">
                        <div class="flex gap-0.75 px-1 py-0.5 ">
                            <p class="tracking-wide dark:text-slate-200 break-all font-medium line-clamp-1">
                                {{ thread.user.name }}</p>
                            <Badge class="shrink-0 dark:text-dark-blue"
                                   :value="getUserStatusByIdOrStatus(thread.user.id, thread.user.is_online) ? 'Online' : 'Offline'"
                                   :type="getUserStatusByIdOrStatus(thread.user.id, thread.user.is_online) ? 'success': 'danger'"/>
                        </div>
                        <div class="text-xs dark:text-slate-200 tracking-wide font-light font-condensed px-1">
                            <span v-if="thread.user.reviews_count < 1">{{ $t('no reviews') }}</span>
                            <span v-else>{{ $t('reviews') }}: {{ thread.user.reviews_count }}</span>
                        </div>
                    </div>
                </div>
                <slot name="right"></slot>
            </template>
            <template v-else>
                <div class="w-full">
                    <div class="flex justify-between pb-1 lg:hidden">
                        <slot name="left"></slot>
                        <slot name="right"></slot>
                    </div>
                    <div class="grid grid-cols-2 gap-2 w-full">
                        <template v-for="participant in thread?.participants">
                            <div class="flex gap-2.5 flex-col lg:flex-row">
                                <div class="flex gap-4 items-center pl-2 mx-auto lg:m-0">
                                    <div
                                        class="rounded-full border border-gray-400 shadow-md-1 min-h-[3.75rem] min-w-[3.75rem] overflow-hidden bg-slate-300 dark:bg-slate-700">
                                        <img class="w-[3.75rem] h-[3.75rem]" :src="participant.user.profile_photo_url"
                                             :alt="participant.user.name">
                                    </div>
                                </div>
                                <div>
                                    <div class="flex gap-0.75 px-1 py-0.5 justify-center lg:justify-start">
                                        <p class="tracking-wide break-all font-medium line-clamp-1">{{
                                                participant.user.name
                                            }}</p>
                                        <Badge class="shrink-0 dark:text-dark-blue"
                                               :value="getUserStatusByIdOrStatus(participant.user.id, participant.user.is_online) ? 'Online' : 'Offline'"
                                               :type="getUserStatusByIdOrStatus(participant.user.id, participant.user.is_online) ? 'success': 'danger'"/>
                                    </div>
                                    <div
                                        class="px-1 pb-1 flex items-center border-b border-dashed border-slate-300 dark:border-slate-700 w-fit">
                                        <div class="flex items-center gap-1 leading-full">
                                            <StarReview class="w-[0.75rem] h-[0.75rem]"
                                                        :class="getStyleStarReview(participant.user.rating)"/>
                                            <div class="text-xs leading-full pt-0.5">{{ participant.user.rating }}</div>
                                        </div>
                                        <div class="py-0.5 px-1 text-xs leading-full text-slate-500">
                                            {{ participant.user.on_site_desc }}
                                        </div>
                                    </div>
                                    <div class="p-1 flex flex-col gap-1 text-xs leading-full font-medium">
                                        <div class="flex gap-2 text-green-500">
                                            <span>{{ $t('Purchases') }}: {{ participant.user.orders_count }}</span>
                                            <span>{{
                                                    activeCurrency.symbol
                                                }} {{ priceStr(((participant.user.orders_amount / 100) * page.props.currency.rate) * 100) }}</span>
                                        </div>
                                        <div class="flex gap-2 text-red-500">
                                            <span>{{ $t('Sales') }}: {{ participant.user.orders_count }}</span>
                                            <span>{{ activeCurrency.symbol }} {{
                                                    priceStr(((participant.user.sells_amount  / 100) * page.props.currency.rate) * 100)
                                                }}</span></div>

                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

            </template>
        </div>
        <div v-if="route().current('threads.*') && thread?.user?.offer"
             class="px-2 py-1 text-sm tracking-wider leading-120 font-normal">
            <span class="text-slate-500 dark:text-slate-400 leading-120">{{ $t('Now watching') }}: </span>
            <Link :href="route('offers.show', thread?.user?.offer.id)">
                <span class="text-blue-600 leading-120 dark:text-cyan-300">{{ thread?.user?.offer.title }}</span>
            </Link>
        </div>
        <div ref="dialogElement"
             class="justify-end rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-dark-blue p-2.5  gap-4 tracking-wider overflow-x-auto relative h-full">
            <div v-if="thread" class="overflow flex flex-col gap-4"
                 :class="viewOffer && messages?.length === 0 ? 'min-h-[7.5rem] lg:min-h-[40vh]' : 'min-h-[40vh]'">
                <template v-if="Object.keys(messages).length === 0">
                    <div
                        class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-500 text-sm tracking-wider font-normal whitespace-nowrap">
                        {{ $t('Send a message to the seller before purchasing') }}
                    </div>
                </template>
                <template v-for="(message, , index) in messages">
                    <message-date
                        v-if="dateComparison(messages[Object.keys(messages)[index - 1]]?.created_at, message?.created_at)"
                        :date="dayjs(message?.created_at).format('D MMMM')"/>
                    <MessageSystem :participants="thread.participants" v-if="isSystem(message)"
                                   :message="message"/>
                    <MessageString @showImg="showImg($event)" v-else
                                   :message="message"></MessageString>

                </template>
            </div>

            <template v-else>
                <AkLogo class="w-[220px] h-[256px] logo-gray dark:!fill-red-500 my-12 mx-auto"></AkLogo>
                <div v-if="haveChat"
                     class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-base tracking-wider dark:text-slate-200 font-medium whitespace-nowrap">
                    {{ $t('Select a chat in the left column') }}
                </div>
            </template>
        </div>
        <div class="flex pl-2.5 gap-2.5 items-center" ref="inputMsg">
            <component :is="VueUploadComponent"
                       v-if="VueUploadComponent"
                class="!flex items-center cursor-pointer h-fit hover:bg-slate-300 dark:hover:bg-slate-700 rounded-lg transition-colors"
                extensions="jpg,jpeg,png,pdf"
                accept="image/png,image/jpg,image/jpeg,application/pdf"
                :multiple="false"
                :size="1024 * 1024 * 10"
                v-model="files"
                @input-filter="inputFilter"
                :disabled="!thread"
                ref="upload">
                <clip class="shrink-0"/>
            </component>

            <Input v-model="formMessage.message"
                   :placeholder="$t('Enter message...')"
                   @onFocus="isAuth"
                   @push="sendMsg"
            >
                <template #right-icon>
                    <button @click="sendMsg">
                        <Send :class="{'fill-blue-600 dark:!fill-cyan-300': formMessage.message?.length > 0}"
                              class="dark:fill-slate-700"/>
                    </button>
                </template>
            </Input>


        </div>
        <Loading v-if="loading"></Loading>
    </div>

    <Modal :show="showModal" @close="closeModal()">
        <img v-if="!dataImg.includes('.pdf')" class="m-auto" :src="dataImg" :alt="$t('Picture')">
        <div class="h-[80vh] lg:h-[60vh]" v-if="dataImg.includes('.pdf')">
            <embed class="w-full h-full" :src="dataImg" type="">
        </div>
    </Modal>

    <Modal :show="needAuth" max-width="300" @close="needAuth = false">
        <div class="dark:text-slate-200 py-5 px-3 flex flex-col gap-3">
            <div class="px-2.5 text-sm font-normal leading-120">
                {{ $t('You must be logged in to send chat messages.') }}
            </div>
            <div class="flex m-auto w-full px-2">
                <Button :text="$t('Sign in')" type="primary" @click="router.get(route('login'))"
                        class="text-sm w-full"/>
            </div>
        </div>
    </Modal>

</template>

<style scoped>

</style>
